"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import {
  Search,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Upload,
  FolderPlus,
  Folder,
  X,
  FileText,
  Loader2,
  CheckCircle,
  AlertCircle,
  Clock,
  Globe,
  RefreshCw,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { useAppAuth } from "@/hooks/useAppAuth";
import { Label } from "../components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import api from "../http";
import type { WebsiteCrawlJob } from "../http";
import SettingsDropdown from "../components/assistant/SettingsDropdown";
import ProfileImageModal from "../components/assistant/ProfileImageModal";
import { useColor } from "@/contexts/ColorContext";
import { appConfig } from "@/appConfig";
import { useToast } from "@/hooks/use-toast";
import { useI18n } from "@/contexts/I18nContext";

const TERMINAL_WEBSITE_CRAWL_STATUSES = new Set([
  "completed",
  "failed",
  "cancelled",
  "deleted",
]);

interface Document {
  id: string;
  name: string;
  user_id?: string | null;
  file_path: string;
  file_type: string;
  file_size?: number;
  status?: string;
  created_at: string;
  updated_at?: string;
  email?: string | null;
  profile_image_url?: string | null;
  tools?: string[] | null;
  folder_id?: string | null;
  folder_name?: string | null;
}

interface DocumentFolder {
  id: string;
  name: string;
  parent_folder_id?: string | null;
  documents_count: number;
}

const ROOT_FOLDER_ID = "__root__";
const ROOT_FOLDER_LABEL = "Root";

const normalizeWebsiteDomain = (value: string) => {
  try {
    const withProtocol = /^https?:\/\//i.test(value) ? value : `https://${value}`;
    return new URL(withProtocol).hostname.toLowerCase().replace(/^www\./, "");
  } catch {
    return "";
  }
};

export default function Documents() {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [isDocumentsLoading, setIsDocumentsLoading] = useState(false);
  const { getAccessToken, user } = useAppAuth();

  // Modal + upload state
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isCreateFolderModalOpen, setIsCreateFolderModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<FileList | null>(null);
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedTools, setSelectedTools] = useState<string[]>([]);
  const [isWebsiteCrawlModalOpen, setIsWebsiteCrawlModalOpen] = useState(false);
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [isStartingWebsiteCrawl, setIsStartingWebsiteCrawl] = useState(false);
  const [isLoadingActiveWebsiteCrawl, setIsLoadingActiveWebsiteCrawl] = useState(true);
  const [websiteCrawlJob, setWebsiteCrawlJob] = useState<WebsiteCrawlJob | null>(null);
  const [isCancellingWebsiteCrawl, setIsCancellingWebsiteCrawl] = useState(false);
  const [selectedFolderCrawl, setSelectedFolderCrawl] = useState<WebsiteCrawlJob | null>(null);
  const [isRecrawling, setIsRecrawling] = useState(false);
  const websiteCrawlStartInFlightRef = useRef(false);

  // Pagination and filtering
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalDocuments, setTotalDocuments] = useState(0);
  const [nextPage, setNextPage] = useState(1);
  const [previousPage, setPreviousPage] = useState(1);
  const [documentFilter, setDocumentFilter] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [folders, setFolders] = useState<DocumentFolder[]>([]);
  const [unassignedCount, setUnassignedCount] = useState(0);
  const [selectedFolder, setSelectedFolder] = useState<string>(ROOT_FOLDER_ID);
  const [newFolderName, setNewFolderName] = useState("");
  const [newFolderParentId, setNewFolderParentId] = useState<string>("");
  const [uploadTargetFolderId, setUploadTargetFolderId] = useState<string>("");
  const [allowedTools, setAllowedTools] = useState<string[]>([]);
  const [expandedFolders, setExpandedFolders] = useState<Record<string, boolean>>({});
  const [expandedTagsByDocument, setExpandedTagsByDocument] = useState<Record<string, boolean>>({});
  const [isMobileTreeOpen, setIsMobileTreeOpen] = useState(false);

  // Current user state for settings dropdown
  const [currentUser, setCurrentUser] = useState<{
    id: string;
    first_name?: string | null;
    last_name?: string | null;
    email: string;
    profile_image_url?: string | null;
  } | null>(null);

  // Profile image modal state
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);

  const { themeColor } = useColor();
  const APP_COLOR = appConfig.features.isDemoMode
    ? themeColor
    : appConfig.themeColor;
  const { toast } = useToast();
  const { t, locale } = useI18n();
  const activeWebsiteCrawlJob =
    websiteCrawlJob &&
    !TERMINAL_WEBSITE_CRAWL_STATUSES.has(websiteCrawlJob.status)
      ? websiteCrawlJob
      : null;
  const websiteCrawlPageTarget =
    activeWebsiteCrawlJob?.pages_found || Number(activeWebsiteCrawlJob?.options?.max_pages) || 500;
  const websiteCrawlProgressPercent = (() => {
    if (!activeWebsiteCrawlJob) return 0;
    const pageProgress = Math.min(
      90,
      Math.round((activeWebsiteCrawlJob.pages_crawled / Math.max(websiteCrawlPageTarget, 1)) * 90),
    );
    const phaseMinimums: Record<string, number> = {
      queued: 2,
      running: 5,
      downloading_pdfs: 90,
      building_document: 94,
      uploading: 96,
      processing: 98,
      cancelling: Math.max(pageProgress, 5),
    };
    return Math.max(pageProgress, phaseMinimums[activeWebsiteCrawlJob.status] ?? pageProgress);
  })();
  const CRAWL_PHASE_TITLES: Record<string, string> = {
    queued: "Preparing crawl for",
    running: "Crawling",
    downloading_pdfs: "Downloading PDFs from",
    building_document: "Building website document for",
    uploading: "Uploading files for",
    processing: "Processing and indexing",
    cancelling: "Stopping crawl for",
  };
  const CRAWL_PHASE_LABELS: Record<string, string> = {
    queued: "Queued",
    running: "Crawling pages",
    downloading_pdfs: "Downloading PDFs",
    building_document: "Building document",
    uploading: "Uploading files",
    processing: "Processing documents",
    cancelling: "Stopping",
  };
  const formatCrawlPhase = (status: string) =>
    CRAWL_PHASE_LABELS[status] ?? status.replace(/_/g, " ");
  const formatCrawlTitle = (job: WebsiteCrawlJob) =>
    `${CRAWL_PHASE_TITLES[job.status] ?? formatCrawlPhase(job.status)} ${job.domain}`;
  const formatCrawlTimestamp = (value?: string | null) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleString(locale === "de" ? "de-CH" : undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  };

  const TOOL_CATEGORIES = [
    "Verband allgemein",
    "Europa",
    "Schifffahrt",
    "Air",
    "Warehouse & Solutions",
    "Recht & Versicherung",
    "Informatik",
    "Betriebswirtschaft",
    "Bildung",
    "Zoll",
  ];

  const normalizeTool = (tool: string) =>
    tool.toLowerCase().replace(/[^a-z0-9]/g, "");

  const allowedToolSet = useMemo(
    () => new Set(allowedTools.map(normalizeTool)),
    [allowedTools],
  );
  const isSpedlogSession = allowedTools.length > 0;
  const availableToolCategories = useMemo(
    () =>
      isSpedlogSession
        ? TOOL_CATEGORIES.filter((category) =>
            allowedToolSet.has(normalizeTool(category)),
          )
        : TOOL_CATEGORIES,
    [isSpedlogSession, allowedToolSet],
  );
  const websiteCrawlBlocksStart = useMemo(() => {
    if (!websiteCrawlJob) return false;
    const blockedStatuses = new Set([
      "queued",
      "running",
      "downloading_pdfs",
      "building_document",
      "uploading",
      "processing",
    ]);
    return (
      (blockedStatuses.has(websiteCrawlJob.status) || Boolean(websiteCrawlJob.folder_id)) &&
      normalizeWebsiteDomain(websiteUrl.trim()) === websiteCrawlJob.domain
    );
  }, [websiteCrawlJob, websiteUrl]);

  const foldersByParent = useMemo(() => {
    const map: Record<string, DocumentFolder[]> = {};
    folders.forEach((folder) => {
      const key = folder.parent_folder_id || ROOT_FOLDER_ID;
      if (!map[key]) map[key] = [];
      map[key].push(folder);
    });
    Object.values(map).forEach((list) =>
      list.sort((a, b) => a.name.localeCompare(b.name)),
    );
    return map;
  }, [folders]);

  const flattenFolders = useMemo(() => {
    const result: Array<{ id: string; name: string; depth: number }> = [];
    const walk = (parentKey: string, depth: number) => {
      const items = foldersByParent[parentKey] || [];
      items.forEach((item) => {
        result.push({ id: item.id, name: item.name, depth });
        walk(item.id, depth + 1);
      });
    };
    walk(ROOT_FOLDER_ID, 0);
    return result;
  }, [foldersByParent]);

  // Load current user information
  useEffect(() => {
    const loadCurrentUser = async () => {
      try {
        const token = await getAccessToken();
        const userData = await api.getCurrentUser(token);
        setCurrentUser(userData);
      } catch (error) {
        console.error("Failed to load current user:", error);
      }
    };

    try {
      const rawSession = localStorage.getItem("sso_session");
      if (rawSession) {
        const parsed = JSON.parse(rawSession);
        const sessionTools = parsed?.user?.tools;
        if (Array.isArray(sessionTools)) {
          setAllowedTools(
            sessionTools
              .map((tool: unknown) => String(tool).trim())
              .filter(Boolean),
          );
        }
      }
    } catch (error) {
      console.error("Failed to parse SSO session tools:", error);
    }

    if (user) {
      loadCurrentUser();
    }
  }, [user, getAccessToken]);

  const handleProfileImageUpdate = (imageUrl: string) => {
    if (currentUser) {
      setCurrentUser({
        ...currentUser,
        profile_image_url: imageUrl,
      });
    }
  };

  const handleUserInfoUpdate = (updatedUser: {
    id: string;
    first_name?: string | null;
    last_name?: string | null;
    email: string;
    profile_image_url?: string | null;
  }) => {
    setCurrentUser(updatedUser);
  };

  const fetchDocuments = async () => {
    const token = await getAccessToken();
    setIsDocumentsLoading(true);
    try {
      const folderFilter =
        selectedFolder === ROOT_FOLDER_ID ? "unassigned" : selectedFolder;
      const data = await api.getDocuments(
        token,
        currentPage,
        documentFilter,
        folderFilter,
        sortField,
        sortDirection,
      );
      if (data) {
        setDocuments(data.documents);
        setTotalPages(data.totalPages);
        setTotalDocuments(data.totalDocuments || 0);
        setNextPage(data.nextPage);
        setPreviousPage(data.previousPage);
      }
    } catch (error) {
      console.error("Error fetching Documents:", error);
    } finally {
      setIsDocumentsLoading(false);
    }
  };

  const fetchFolders = async () => {
    const token = await getAccessToken();
    try {
      const data = await api.getDocumentFolders(token);
      const fetchedFolders = data.folders || [];
      setFolders(fetchedFolders);
      setUnassignedCount(data.unassigned_count || 0);
      setExpandedFolders((prev) => {
        const next = { ...prev };
        fetchedFolders.forEach((folder: DocumentFolder) => {
          if (next[folder.id] === undefined) {
            next[folder.id] = true;
          }
        });
        return next;
      });
    } catch (error) {
      console.error("Error fetching document folders:", error);
    }
  };

  const handleSort = (field: string, direction: 'asc' | 'desc') => {
    setSortField(field);
    setSortDirection(direction);
  };

  useEffect(() => {
    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, documentFilter, selectedFolder, sortField, sortDirection]);

  useEffect(() => {
    fetchFolders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadActiveWebsiteCrawl = async () => {
      try {
        const token = await getAccessToken();
        const result = await api.getActiveWebsiteCrawl(token);
        if (!cancelled) {
          setWebsiteCrawlJob(result.job);
        }
      } catch (error) {
        console.error("Failed to load active website crawl:", error);
      } finally {
        if (!cancelled) {
          setIsLoadingActiveWebsiteCrawl(false);
        }
      }
    };

    loadActiveWebsiteCrawl();

    return () => {
      cancelled = true;
    };
  }, [getAccessToken]);

  useEffect(() => {
    if (!isSpedlogSession) return;
    setSelectedTools((prev) =>
      prev.filter((tool) => availableToolCategories.includes(tool)),
    );
  }, [isSpedlogSession, availableToolCategories]);

  useEffect(() => {
    if (!websiteCrawlJob?.job_id) return;
    if (TERMINAL_WEBSITE_CRAWL_STATUSES.has(websiteCrawlJob.status)) return;

    let cancelled = false;
    const pollWebsiteCrawl = async () => {
      try {
        const token = await getAccessToken();
        const nextJob = await api.getWebsiteCrawlStatus(websiteCrawlJob.job_id, token);
        if (cancelled) return;
        setWebsiteCrawlJob(nextJob);

        if (nextJob.status === "completed") {
          await fetchFolders();
          await fetchDocuments();
          toast({
            title: "Website crawl completed",
            description: `${nextJob.domain} is ready in Documents.`,
          });
          setWebsiteCrawlJob(null);
        }

        if (nextJob.status === "failed") {
          toast({
            title: "Website crawl failed",
            description: nextJob.error || "Crawler stopped before completion.",
            variant: "destructive",
          });
          setWebsiteCrawlJob(null);
        }

        if (nextJob.status === "cancelled") {
          await fetchFolders();
          await fetchDocuments();
          toast({
            title: "Website crawl cancelled",
            description: `Crawler stopped for ${nextJob.domain}.`,
          });
          setWebsiteCrawlJob(null);
        }

        if (nextJob.status === "deleted") {
          await fetchFolders();
          await fetchDocuments();
          setWebsiteCrawlJob(null);
        }
      } catch (error) {
        console.error("Failed to poll website crawl status:", error);
      }
    };

    pollWebsiteCrawl();
    const interval = setInterval(pollWebsiteCrawl, 2500);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [websiteCrawlJob?.job_id, websiteCrawlJob?.status]);

  // Load the website-crawl metadata for the selected folder (if any), so the
  // folder header can show last/next re-crawl and offer a manual re-crawl.
  useEffect(() => {
    if (selectedFolder === ROOT_FOLDER_ID || selectedFolder === "unassigned") {
      setSelectedFolderCrawl(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const token = await getAccessToken();
        const { job } = await api.getWebsiteCrawlByFolder(selectedFolder, token);
        if (!cancelled) setSelectedFolderCrawl(job);
      } catch (error) {
        if (!cancelled) setSelectedFolderCrawl(null);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFolder, websiteCrawlJob?.status]);

  // Poll for status updates if there are processing documents
  useEffect(() => {
    const hasProcessingDocs = documents.some(
      (doc) => doc.status === "pending" || doc.status === "processing",
    );

    if (hasProcessingDocs) {
      const interval = setInterval(() => {
        fetchDocuments();
      }, 2000); // Poll every 2 seconds for faster updates

      return () => clearInterval(interval);
    }
  }, [documents]);

  // Handle the actual file(s) uploading
  const handleFiles = async (files: File[]) => {
    if (isUploading || files.length === 0) return;

    setIsUploading(true);

    try {
      const token = await getAccessToken();
      const response = await api.upload(
        files,
        token,
        uploadTargetFolderId || null,
        selectedTools,
      );

      if (response.failed.length > 0) {
        const failedNames = response.failed
          .map((f) => `${f.filename} (${f.reason})`)
          .join(", ");
        toast({
          title: t("documents.uploadPartialFailed"),
          description: t("documents.uploadPartialFailedDescription", {
            count: response.failed.length,
            failedNames,
          }),
          variant: "destructive",
        });
      }

      if (response.uploaded.length > 0) {
        toast({
          title: t("documents.uploadSuccess"),
          description: t("documents.uploadSuccessDescription", {
            count: response.uploaded.length,
          }),
        });
      }

      clearAllFiles();
      setIsUploadModalOpen(false);
      await fetchDocuments();
      await fetchFolders();
    } catch (error) {
      console.error("Error uploading files:", error);
      toast({
        title: t("documents.uploadFailed"),
        description:
          error instanceof Error ? error.message : t("settings.unknownError"),
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  // Called when user clicks "Upload" button in modal
  const handleUpload = () => {
    if (!selectedFile) return;

    if (isSpedlogSession && selectedTools.length === 0) {
      toast({
        title: t("documents.categoryRequired"),
        description: t("documents.categoryRequiredDescription"),
        variant: "destructive",
      });
      return;
    }

    handleFiles(uploadFiles);
  };

  const handleStartWebsiteCrawl = async () => {
    if (websiteCrawlStartInFlightRef.current || websiteCrawlBlocksStart) return;

    const trimmedUrl = websiteUrl.trim();
    if (!trimmedUrl) {
      toast({
        title: "Website URL required",
        description: "Enter a website URL before starting the crawler.",
        variant: "destructive",
      });
      return;
    }

    if (isSpedlogSession && selectedTools.length === 0) {
      toast({
        title: t("documents.categoryRequired"),
        description: t("documents.categoryRequiredDescription"),
        variant: "destructive",
      });
      return;
    }

    websiteCrawlStartInFlightRef.current = true;
    setIsStartingWebsiteCrawl(true);
    try {
      const token = await getAccessToken();
      const job = await api.startWebsiteCrawl(
        {
          url: trimmedUrl,
          max_pages: 500,
          include_linked_pdfs: true,
          include_external_pdfs: false,
          max_linked_pdfs: 200,
          max_pdf_size_mb: 50,
          tools: selectedTools,
        },
        token,
      );
      setWebsiteCrawlJob(job);
      setIsWebsiteCrawlModalOpen(false);
      toast({
        title: "Website crawl started",
        description: `Crawler is running for ${job.domain}.`,
      });
    } catch (error) {
      toast({
        title: "Website crawl failed to start",
        description:
          error instanceof Error ? error.message : t("settings.unknownError"),
        variant: "destructive",
      });
    } finally {
      websiteCrawlStartInFlightRef.current = false;
      setIsStartingWebsiteCrawl(false);
    }
  };

  const handleCancelWebsiteCrawl = async () => {
    if (!websiteCrawlJob?.job_id || isCancellingWebsiteCrawl) return;
    setIsCancellingWebsiteCrawl(true);
    try {
      const token = await getAccessToken();
      const updated = await api.cancelWebsiteCrawl(websiteCrawlJob.job_id, token);
      if (updated.status === "deleted") {
        setWebsiteCrawlJob(null);
        await fetchFolders();
        await fetchDocuments();
      } else {
        setWebsiteCrawlJob(updated);
      }
      toast({
        title: "Website crawl stopped",
        description: `${updated.domain} was removed from Documents.`,
      });
    } catch (error) {
      toast({
        title: "Could not cancel crawl",
        description:
          error instanceof Error ? error.message : t("settings.unknownError"),
        variant: "destructive",
      });
    } finally {
      setIsCancellingWebsiteCrawl(false);
    }
  };

  const handleRecrawlFolder = async () => {
    if (!selectedFolderCrawl?.job_id || isRecrawling) return;
    setIsRecrawling(true);
    try {
      const token = await getAccessToken();
      const updated = await api.recrawlWebsite(selectedFolderCrawl.job_id, token);
      setSelectedFolderCrawl(updated);
      setWebsiteCrawlJob(updated);
      toast({
        title: "Re-crawl started",
        description: `Refreshing ${updated.domain}. Only changed files will be replaced.`,
      });
    } catch (error) {
      toast({
        title: "Could not start re-crawl",
        description:
          error instanceof Error ? error.message : t("settings.unknownError"),
        variant: "destructive",
      });
    } finally {
      setIsRecrawling(false);
    }
  };

  // IFC file size constants
  const ALLOWED_EXTENSIONS = ["pdf", "doc", "docx", "xlsx", "csv", "ifc"];
  const MAX_FILE_SIZE_DEFAULT = 25 * 1024 * 1024; // 25MB
  const MAX_FILE_SIZE_IFC = 500 * 1024 * 1024; // 500MB for IFC
  const MAX_TOTAL_SIZE_DEFAULT = 100 * 1024 * 1024; // 100MB
  const MAX_TOTAL_SIZE_IFC = 500 * 1024 * 1024; // 500MB when IFC present

  const getExtension = (file: File) =>
    file.name.split(".").pop()?.toLowerCase() || "";

  const getMaxSizeForFile = (file: File) =>
    getExtension(file) === "ifc" ? MAX_FILE_SIZE_IFC : MAX_FILE_SIZE_DEFAULT;

  const getTotalSizeLimit = (files: File[]) =>
    files.some((file) => getExtension(file) === "ifc")
      ? MAX_TOTAL_SIZE_IFC
      : MAX_TOTAL_SIZE_DEFAULT;

  const validateFiles = (files: File[]) => {
    const validFiles = files.filter((file) =>
      ALLOWED_EXTENSIONS.includes(getExtension(file)),
    );

    if (validFiles.length !== files.length) {
      toast({
        title: t("documents.invalidFileType"),
        description: t("documents.invalidFileTypeDescription", {
          extensions: ALLOWED_EXTENSIONS.join(", ").toUpperCase(),
        }),
        variant: "destructive",
      });
    }

    if (validFiles.length === 0) {
      return null;
    }

    const oversizedFiles = validFiles.filter(
      (file) => file.size > getMaxSizeForFile(file),
    );
    if (oversizedFiles.length > 0) {
      toast({
        title: t("documents.fileTooLarge"),
        description: t("documents.fileTooLargeDescription", {
          files: oversizedFiles.map((f) => f.name).join(", "),
        }),
        variant: "destructive",
      });
      return null;
    }

    const totalSizeLimit = getTotalSizeLimit(validFiles);
    const totalSize = validFiles.reduce((sum, file) => sum + file.size, 0);
    if (totalSize > totalSizeLimit) {
      toast({
        title: t("documents.totalSizeExceeded"),
        description: t("documents.totalSizeExceededDescription", {
          limit: Math.round(totalSizeLimit / (1024 * 1024)),
        }),
        variant: "destructive",
      });
      return null;
    }

    return validFiles;
  };

  // Called when user selects a file
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    if (files.length === 0) return;

    const validFiles = validateFiles(files);
    if (!validFiles) return;

    setSelectedFile(e.target.files);
    setUploadFiles(validFiles);
  };

  // Add function to remove individual files
  const removeFile = (indexToRemove: number) => {
    const newFiles = uploadFiles.filter((_, index) => index !== indexToRemove);
    setUploadFiles(newFiles);

    // Update FileList for consistency
    if (newFiles.length === 0) {
      setSelectedFile(null);
    } else {
      const dt = new DataTransfer();
      newFiles.forEach((file) => dt.items.add(file));
      setSelectedFile(dt.files);
    }
  };

  // Add function to clear all files
  const clearAllFiles = () => {
    setUploadFiles([]);
    setSelectedFile(null);
    setSelectedTools([]);
    setUploadTargetFolderId("");
  };

  // Add drag & drop handlers
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const droppedFiles = Array.from(e.dataTransfer.files);

    const validDroppedFiles = validateFiles(droppedFiles);
    if (!validDroppedFiles) return;

    // Add to existing files
    const newFiles = [...uploadFiles, ...validDroppedFiles];

    // Validate total size with combined files
    const totalSizeLimit = getTotalSizeLimit(newFiles);
    const totalSize = newFiles.reduce((sum, file) => sum + file.size, 0);
    if (totalSize > totalSizeLimit) {
      toast({
        title: t("documents.totalSizeExceeded"),
        description: t("documents.totalSizeExceededDescription", {
          limit: Math.round(totalSizeLimit / (1024 * 1024)),
        }),
        variant: "destructive",
      });
      return;
    }

    setUploadFiles(newFiles);

    // Update FileList
    const dt = new DataTransfer();
    newFiles.forEach((file) => dt.items.add(file));
    setSelectedFile(dt.files);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // Deletion handler
  const handleDelete = async (documentId: string) => {
    try {
      const token = await getAccessToken();
      const result = await api.deleteDocuments(documentId, token);
      if (result) {
        setDocuments((prevDocs) =>
          prevDocs.filter((doc) => doc.id !== documentId),
        );
        await fetchFolders();
        toast({
          title: t("documents.deleteSuccess"),
          description: t("documents.deleteSuccessDescription"),
        });
      }
    } catch (error) {
      console.error("Error deleting Document:", error);
      toast({
        title: t("documents.deleteFailed"),
        description: t("documents.deleteFailedDescription"),
        variant: "destructive",
      });
    }
  };

  // Document click handler - view/download document
  const handleDocumentClick = async (documentId: string, documentName: string) => {
    try {
      const token = await getAccessToken();
      
      // Check if it's a PDF based on file extension
      const isPdf = documentName.toLowerCase().endsWith('.pdf');
      
      if (isPdf) {
        // For PDFs: Open URL directly in new tab so headers are preserved
        const downloadUrl = `${import.meta.env.VITE_BACKEND_URL}/documents/download/${documentId}`;
        
        // Create a temporary form to POST the request with auth token
        // This is more reliable than window.open for authenticated requests
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        
        // For authenticated requests, we need to use fetch and blob
        const response = await fetch(downloadUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        
        if (!response.ok) {
          throw new Error(`Failed to open PDF: ${documentName}`);
        }
        
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        
        // Open in new tab - browser will display PDF inline
        const newWindow = window.open(blobUrl, '_blank');
        
        // Clean up blob URL after window opens
        if (newWindow) {
          newWindow.onload = () => {
            // Give the browser time to load the PDF before revoking the URL
            setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
          };
        } else {
          // If popup was blocked, clean up immediately
          setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
        }
      } else {
        // For other files: Download with original filename
        const downloadUrl = `${import.meta.env.VITE_BACKEND_URL}/documents/download/${documentId}`;
        
        const response = await fetch(downloadUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        
        if (!response.ok) {
          throw new Error(`Failed to download document: ${documentName}`);
        }
        
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        
        // Trigger download with original filename
        const link = document.createElement('a');
        link.href = url;
        link.download = documentName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Clean up
        setTimeout(() => window.URL.revokeObjectURL(url), 100);
      }
      
    } catch (error) {
      console.error(`Error downloading document "${documentName}":`, error);
      toast({
        title: t("documents.openFailed"),
        description: t("documents.openFailedDescription", { name: documentName }),
        variant: "destructive",
      });
    }
  };

  // Get status badge component
  const getStatusBadge = (status: string | undefined) => {
    switch (status) {
      case "pending":
        return (
          <div className="flex items-center gap-1 text-yellow-600 bg-yellow-50 px-2 py-1 rounded-full text-xs">
            <Clock size={12} />
            Ausstehend
          </div>
        );
      case "processing":
        return (
          <div className="flex items-center gap-1 text-blue-600 bg-blue-50 px-2 py-1 rounded-full text-xs">
            <Loader2 size={12} className="animate-spin" />
            Wird verarbeitet
          </div>
        );
      case "failed":
        return (
          <div className="flex items-center gap-1 text-red-600 bg-red-50 px-2 py-1 rounded-full text-xs">
            <AlertCircle size={12} />
            Fehlgeschlagen
          </div>
        );
      case "permanent_failed":
        return (
          <div className="flex items-center gap-1 text-red-800 bg-red-100 px-2 py-1 rounded-full text-xs">
            <AlertCircle size={12} />
            {t("documents.permanentFailed")}
          </div>
        );
      case "processed":
      default:
        return (
          <div className="flex items-center gap-1 text-green-600 bg-green-50 px-2 py-1 rounded-full text-xs">
            <CheckCircle size={12} />
            Verarbeitet
          </div>
        );
    }
  };

  // Count documents by status
  const processingCount = documents.filter(
    (doc) => doc.status === "pending" || doc.status === "processing",
  ).length;

  // Update the modal close handler
  const closeUploadModal = () => {
    setIsUploadModalOpen(false);
    clearAllFiles();
  };

  const createFolder = async () => {
    const trimmed = newFolderName.trim();
    if (!trimmed) return;
    const parentFolderId = newFolderParentId || null;
    try {
      const token = await getAccessToken();
      const response = await api.createDocumentFolder(trimmed, parentFolderId, token);
      const createdId = response?.folder?.id;
      setNewFolderName("");
      setNewFolderParentId("");
      setIsCreateFolderModalOpen(false);
      if (createdId) {
        setSelectedFolder(createdId);
      }
      toast({
        title: t("documents.folderCreated"),
        description: t("documents.folderCreatedDescription", { name: trimmed }),
      });
      await fetchFolders();
      await fetchDocuments();
    } catch (error) {
      toast({
        title: t("documents.folderCreateFailed"),
        description:
          error instanceof Error ? error.message : t("settings.unknownError"),
        variant: "destructive",
      });
    }
  };

  const deleteSelectedFolder = async () => {
    if (selectedFolder === ROOT_FOLDER_ID) return;
    if (!selectedFolderDetails) return;

    const docCount = selectedFolderDetails.documents_count ?? 0;
    const confirmed = window.confirm(
      `${t("documents.folderDeleteConfirm", { name: selectedFolderDetails.name })}\n\n` +
        `This permanently deletes the folder, all of its documents` +
        `${docCount ? ` (${docCount})` : ""} and any subfolders. This cannot be undone.`,
    );
    if (!confirmed) return;

    try {
      const token = await getAccessToken();
      await api.deleteDocumentFolder(selectedFolderDetails.id, token);
      setSelectedFolder(ROOT_FOLDER_ID);
      setCurrentPage(1);
      await fetchFolders();
      await fetchDocuments();
      toast({
        title: t("documents.folderDeleted"),
        description: t("documents.folderDeletedDescription", {
          name: selectedFolderDetails.name,
        }),
      });
    } catch (error) {
      toast({
        title: t("documents.folderDeleteFailed"),
        description:
          error instanceof Error ? error.message : t("settings.unknownError"),
        variant: "destructive",
      });
    }
  };

  const displayedDocuments = documents;
  const selectedFolderDetails = useMemo(
    () => folders.find((folder) => folder.id === selectedFolder) || null,
    [folders, selectedFolder],
  );
  const selectedFolderDisplayName =
    selectedFolder === ROOT_FOLDER_ID
      ? ROOT_FOLDER_LABEL
      : selectedFolderDetails?.name || ROOT_FOLDER_LABEL;
  const selectedChildFolders = useMemo(() => {
    if (selectedFolder === ROOT_FOLDER_ID) return foldersByParent[ROOT_FOLDER_ID] || [];
    return foldersByParent[selectedFolder] || [];
  }, [selectedFolder, foldersByParent]);
  const desktopTableGridClass =
    "grid grid-cols-[2.8fr_1.5fr_1.2fr_1.6fr_1fr_92px]";
  const getEmailText = (document: Document) => document.email || t("documents.unknown");
  const getEmailInitial = (document: Document) =>
    getEmailText(document).charAt(0).toUpperCase();

  const toggleFolderExpanded = (folderId: string) => {
    setExpandedFolders((prev) => ({
      ...prev,
      [folderId]: !prev[folderId],
    }));
  };

  const renderFolderTree = (parentKey: string, depth = 0) => {
    const children = foldersByParent[parentKey] || [];
    if (children.length === 0) return null;

    return children.map((folder) => {
      const hasChildren = (foldersByParent[folder.id] || []).length > 0;
      const isExpanded = expandedFolders[folder.id] !== false;
      const isSelected = selectedFolder === folder.id;

      return (
        <div key={folder.id}>
          <div
            className={`flex items-center justify-between gap-2 rounded-md border px-2 py-1.5 cursor-pointer ${
              isSelected
                ? "text-white"
                : "border-transparent text-gray-700 hover:bg-gray-100"
            }`}
            style={{
              marginLeft: depth * 16,
              ...(isSelected
                ? {
                    backgroundColor: APP_COLOR,
                    borderColor: APP_COLOR,
                  }
                : {}),
            }}
            onClick={() => {
              setSelectedFolder(folder.id);
              setCurrentPage(1);
              setIsMobileTreeOpen(false);
            }}
          >
            <div className="flex items-center gap-1 min-w-0">
              {hasChildren ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleFolderExpanded(folder.id);
                  }}
                  className="rounded border border-transparent bg-white p-0.5 hover:bg-slate-50"
                  style={isSelected ? { color: APP_COLOR } : undefined}
                >
                  {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </button>
              ) : (
                <span className="w-[18px]" />
              )}
              <Folder size={14} className="shrink-0" />
              <span className="text-sm truncate">{folder.name}</span>
            </div>
            <span className="text-xs shrink-0">({folder.documents_count || 0})</span>
          </div>
          {hasChildren && isExpanded && renderFolderTree(folder.id, depth + 1)}
        </div>
      );
    });
  };

  const folderNavigation = (
    <div className="border border-gray-200 rounded-lg p-2 bg-white">
      <div
        className={`flex items-center justify-between gap-2 rounded-md border px-2 py-1.5 cursor-pointer ${
          selectedFolder === ROOT_FOLDER_ID
            ? "text-white"
            : "border-transparent text-gray-700 hover:bg-gray-100"
        }`}
        style={
          selectedFolder === ROOT_FOLDER_ID
            ? {
                backgroundColor: APP_COLOR,
                borderColor: APP_COLOR,
              }
            : undefined
        }
        onClick={() => {
          setSelectedFolder(ROOT_FOLDER_ID);
          setCurrentPage(1);
          setIsMobileTreeOpen(false);
        }}
      >
        <div className="flex items-center gap-1 min-w-0">
          <Folder size={14} className="shrink-0" />
          <span className="text-sm truncate">{ROOT_FOLDER_LABEL}</span>
        </div>
        <span className="text-xs shrink-0">({unassignedCount})</span>
      </div>
      <div className="mt-1">{renderFolderTree(ROOT_FOLDER_ID)}</div>
    </div>
  );

  const openCreateFolderModal = () => {
    setNewFolderName("");
    setNewFolderParentId(
      selectedFolder !== ROOT_FOLDER_ID
        ? selectedFolder
        : "",
    );
    setIsCreateFolderModalOpen(true);
  };

  return (
    <div className="flex h-screen overflow-hidden bg-transparent admindiv">
      <aside className="hidden lg:flex w-80 flex-col border-r border-gray-200 bg-white">
        <div className="px-4 py-4 border-b border-gray-200 space-y-3">
          <div>
            <p className="text-sm font-semibold">{t("documents.folderStructure")}</p>
            <p className="text-xs text-gray-500 truncate">
              {t("documents.currentFolder")}: {selectedFolderDisplayName}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={openCreateFolderModal}
              className="inline-flex items-center justify-center gap-1 rounded-md border px-3 py-2 text-sm text-white shadow-sm"
              style={{
                backgroundColor: APP_COLOR,
                borderColor: APP_COLOR,
              }}
            >
              <FolderPlus size={14} />
              {t("documents.newFolder")}
            </button>
            {selectedFolderDetails && (
              <button
                type="button"
                onClick={deleteSelectedFolder}
                className="inline-flex items-center justify-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-slate-50"
              >
                {t("common.delete")}
              </button>
            )}
          </div>
        </div>
        <div className="flex-1 overflow-auto p-4">
          {folderNavigation}
        </div>
      </aside>
      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header - hidden on mobile */}
        <header className="hidden lg:flex items-center justify-between px-8 py-4 border-b border-gray-200">
          <h1 className="text-xl font-semibold">{t("documents.title")}</h1>
          <div className="flex items-center gap-2">
            <SettingsDropdown
              onOpenProfileModal={() => setIsProfileModalOpen(true)}
              currentUser={currentUser}
            />
          </div>
        </header>

        {/* Content */}
        <div className="flex-1 p-4 lg:p-8 overflow-auto pb-20 lg:pb-8">
          {/* Mobile Header - only show on mobile */}
          <div className="lg:hidden mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">
                  {t("documents.uploadedDocuments")}
                </span>
                <div className="flex items-center bg-[#EEF2FF] rounded-[6px] px-[6px] py-[3px]">
                  <span
                    className={`font-bold text-xs`}
                    style={{ color: APP_COLOR }}
                  >
                    {totalDocuments} {t(totalDocuments === 1 ? "documents.document_one" : "documents.document_other")}
                  </span>
                </div>
                {processingCount > 0 && (
                  <div className="flex items-center gap-1 text-blue-600 bg-blue-50 px-2 py-1 rounded-full text-xs">
                    <Loader2 size={10} className="animate-spin" />
                    {t("documents.processingCount", { count: processingCount })}
                  </div>
                )}
              </div>
              <button
                className="inline-flex h-8 items-center justify-center whitespace-nowrap rounded-[1234px] border px-3 py-1 text-sm font-medium text-white transition-colors"
                style={{
                  backgroundColor: APP_COLOR,
                  borderColor: APP_COLOR,
                }}
                onClick={() => setIsUploadModalOpen(true)}
              >
                <Upload size={14} className="mr-1" />
                <span className="text-xs">{t("documents.upload")}</span>
              </button>
              <button
                className="inline-flex h-8 items-center justify-center whitespace-nowrap rounded-[1234px] border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                onClick={() => setIsWebsiteCrawlModalOpen(true)}
                disabled={isLoadingActiveWebsiteCrawl || Boolean(activeWebsiteCrawlJob)}
              >
                <Globe size={14} className="mr-1" />
                <span className="text-xs">Website</span>
              </button>
            </div>
          </div>

          {!isLoadingActiveWebsiteCrawl && activeWebsiteCrawlJob && (
            <div className="mb-5 rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-blue-950 shadow-sm">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex min-w-0 items-start gap-3">
                  <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-blue-600">
                    <Loader2 size={18} className="animate-spin" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <p className="font-medium">{formatCrawlTitle(activeWebsiteCrawlJob)}</p>
                      <span className="rounded-full bg-white px-2 py-0.5 text-xs font-medium text-blue-700">
                        {formatCrawlPhase(activeWebsiteCrawlJob.status)}
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-blue-800">
                      {activeWebsiteCrawlJob.status === "processing"
                        ? "Crawled content is being processed and indexed for search."
                        : (
                          <>
                            Pages {activeWebsiteCrawlJob.pages_crawled} / {websiteCrawlPageTarget}
                            {" · "}PDFs {activeWebsiteCrawlJob.pdfs_downloaded} / {activeWebsiteCrawlJob.pdfs_found || 200}
                            {" · "}Files {activeWebsiteCrawlJob.files_uploaded}
                          </>
                        )}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 sm:flex-col sm:items-end">
                  <div className="w-full sm:w-44">
                    <div className="h-2 overflow-hidden rounded-full bg-white">
                      <div
                        className="h-full rounded-full bg-blue-600 transition-all"
                        style={{ width: `${websiteCrawlProgressPercent}%` }}
                      />
                    </div>
                    <p className="mt-1 text-right text-xs text-blue-700">
                      {websiteCrawlProgressPercent}%
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleCancelWebsiteCrawl}
                    disabled={
                      isCancellingWebsiteCrawl ||
                      activeWebsiteCrawlJob.status === "cancelling"
                    }
                    className="inline-flex h-8 shrink-0 items-center justify-center gap-1 whitespace-nowrap rounded-md border border-red-200 bg-white px-3 text-xs font-medium text-red-600 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {isCancellingWebsiteCrawl || activeWebsiteCrawlJob.status === "cancelling" ? (
                      <>
                        <Loader2 size={13} className="animate-spin" />
                        Stopping
                      </>
                    ) : (
                      <>
                        <X size={13} />
                        Stop crawl
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {selectedFolderCrawl && selectedFolder !== ROOT_FOLDER_ID && (
            <div className="mb-5 rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <Globe size={15} className="text-gray-500" />
                    <span className="font-medium text-gray-800">
                      {selectedFolderCrawl.domain}
                    </span>
                    {selectedFolderCrawl.auto_recrawl !== false && (
                      <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
                        Auto re-crawl on
                      </span>
                    )}
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                      {formatCrawlPhase(selectedFolderCrawl.status)}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    {formatCrawlTimestamp(selectedFolderCrawl.last_crawled_at)
                      ? `Last crawl: ${formatCrawlTimestamp(selectedFolderCrawl.last_crawled_at)}`
                      : "Not crawled yet"}
                    {" · "}
                    Next scheduled re-crawl: daily 03:00
                    {selectedFolderCrawl.error ? (
                      <span className="text-red-600"> · {selectedFolderCrawl.error}</span>
                    ) : null}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleRecrawlFolder}
                  disabled={
                    isRecrawling ||
                    [
                      "queued",
                      "running",
                      "downloading_pdfs",
                      "building_document",
                      "uploading",
                      "processing",
                      "cancelling",
                    ].includes(selectedFolderCrawl.status)
                  }
                  className="inline-flex h-8 shrink-0 items-center justify-center gap-1 whitespace-nowrap rounded-md border border-gray-300 bg-white px-3 text-xs font-medium text-gray-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isRecrawling ? (
                    <>
                      <Loader2 size={13} className="animate-spin" />
                      Starting
                    </>
                  ) : (
                    <>
                      <RefreshCw size={13} />
                      Re-crawl now
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Desktop Layout */}
          <div className="hidden lg:flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <span className="text-lg font-medium">
                {t("documents.uploadedDocuments")}
              </span>
              <div className="flex items-center bg-[#EEF2FF] rounded-[6px] px-[6px] py-[3px]">
                <span
                  className={`font-bold text-xs`}
                  style={{ color: APP_COLOR }}
                >
                  {totalDocuments} {t(totalDocuments === 1 ? "documents.document_one" : "documents.document_other")}
                </span>
              </div>
              {processingCount > 0 && (
                <div className="flex items-center gap-1 text-blue-600 bg-blue-50 px-2 py-1 rounded-full text-xs">
                  <Loader2 size={12} className="animate-spin" />
                  {t("documents.processingCountParallel", { count: processingCount })}
                </div>
              )}
              <span className="text-xs text-gray-500">
                {t("documents.folder")}: <span className="font-medium">{selectedFolderDisplayName}</span>
              </span>
            </div>

            <div className="relative w-[300px]">
              <input
                type="text"
                placeholder={t("documents.searchPlaceholder")}
                className="w-full pl-4 pr-10 py-2 border border-gray-200 rounded-lg"
                value={searchInput}
                onChange={(e) => {
                  const value = e.target.value;
                  setSearchInput(value);
                  // Only filter if 3+ chars or empty
                  if (value.length >= 3 || value.length === 0) {
                    setDocumentFilter(value);
                    setCurrentPage(1); // reset page
                  }
                }}
              />
              <button className="absolute right-3 top-1/2 transform -translate-y-1/2 p-2 hover:bg-slate-100 rounded-md bg-transparent border-none focus:outline-none active:outline-none text-gray-400 hover:text-gray-600 transition-colors">
                <Search size={18} />
              </button>
            </div>

            <div className="flex items-center gap-4">
              <button
                className="inline-flex h-10 items-center justify-center whitespace-nowrap rounded-[1234px] border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                onClick={() => setIsWebsiteCrawlModalOpen(true)}
                disabled={isLoadingActiveWebsiteCrawl || Boolean(activeWebsiteCrawlJob)}
              >
                <Globe size={16} className="mr-2" />
                <span>Website</span>
              </button>
              <button
                className="inline-flex h-10 items-center justify-center whitespace-nowrap rounded-[1234px] border px-4 py-2 text-sm font-medium text-white transition-colors"
                style={{
                  backgroundColor: APP_COLOR,
                  borderColor: APP_COLOR,
                }}
                onClick={() => setIsUploadModalOpen(true)}
              >
                <Upload size={16} className="mr-2" />
                <span>{t("documents.uploadNew")}</span>
              </button>
            </div>
          </div>

          <div className="lg:hidden mb-4 space-y-3">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={openCreateFolderModal}
                className="inline-flex items-center justify-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-slate-50"
              >
                <FolderPlus size={14} />
                {t("documents.newFolder")}
              </button>
              {selectedFolderDetails && (
                <button
                  type="button"
                  onClick={deleteSelectedFolder}
                  className="inline-flex items-center justify-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-slate-50"
                >
                  {t("documents.deleteFolder")}
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={() => setIsMobileTreeOpen((prev) => !prev)}
              className="w-full flex items-center justify-between border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white"
            >
              <span>
                {t("documents.folder")}: <span className="font-medium">{selectedFolderDisplayName}</span>
              </span>
              {isMobileTreeOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
            {isMobileTreeOpen && (
              <div className="space-y-3">
                {folderNavigation}
              </div>
            )}
          </div>

          {/* Mobile Search - only show on mobile */}
          <div className="lg:hidden mb-4">
            <div className="relative w-full">
              <input
                type="text"
                placeholder={t("documents.searchPlaceholder")}
                className="w-full pl-4 pr-10 py-2 border border-gray-200 rounded-lg"
                value={searchInput}
                onChange={(e) => {
                  const value = e.target.value;
                  setSearchInput(value);
                  // Only filter if 3+ chars or empty
                  if (value.length >= 3 || value.length === 0) {
                    setDocumentFilter(value);
                    setCurrentPage(1); // reset page
                  }
                }}
              />
              <button className="absolute right-3 top-1/2 transform -translate-y-1/2 p-2 hover:bg-slate-100 rounded-md bg-transparent border-none focus:outline-none active:outline-none text-gray-400 hover:text-gray-600 transition-colors">
                <Search size={18} />
              </button>
            </div>
          </div>

          {/* Mobile Document Cards */}
          <div className="lg:hidden flex-1">
            {selectedChildFolders.length > 0 && (
              <div className="space-y-3 mb-4">
                {selectedChildFolders.map((folder) => (
                  <button
                    key={`mobile-folder-${folder.id}`}
                    type="button"
                    onClick={() => {
                      setSelectedFolder(folder.id);
                      setCurrentPage(1);
                    }}
                    className="w-full bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-between shadow-sm"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div
                        className="rounded-md p-2"
                        style={{ backgroundColor: `${APP_COLOR}15`, color: APP_COLOR }}
                      >
                        <Folder size={18} />
                      </div>
                      <div className="min-w-0 text-left">
                        <p className="font-medium truncate">{folder.name}</p>
                        <p className="text-xs text-gray-500">
                          {folder.documents_count ?? 0} {t("documents.document_other")}
                        </p>
                      </div>
                    </div>
                    <ChevronRight size={16} className="text-gray-500" />
                  </button>
                ))}
              </div>
            )}
            {isDocumentsLoading ? (
              <div className="p-8 text-center text-gray-500">
                <Loader2 size={24} className="mx-auto mb-2 animate-spin" />
                {t("documents.loading")}
              </div>
            ) : displayedDocuments.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                {t("documents.empty")}
              </div>
            ) : (
              <div className="space-y-3">
                {displayedDocuments.map((document) => (
                  <div
                    key={document.id}
                    className="bg-white border border-gray-200 rounded-lg p-4"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1 min-w-0">
                        <div 
                          className="flex items-center gap-2 mb-2 cursor-pointer transition-colors group"
                          style={{ ['--hover-color' as string]: APP_COLOR }}
                          onClick={() => handleDocumentClick(document.id, document.name)}
                          onMouseEnter={(e) => e.currentTarget.style.color = APP_COLOR}
                          onMouseLeave={(e) => e.currentTarget.style.color = 'inherit'}
                        >
                          <div className="flex-shrink-0 p-1 rounded-md" style={{ backgroundColor: `${APP_COLOR}20` }}>
                            <FileText
                              size={16}
                              style={{ color: APP_COLOR }}
                            />
                          </div>
                          <span className="font-medium text-sm truncate group-hover:underline">
                            {document.name}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 mb-2">
                          <Avatar className="w-6 h-6">
                            <AvatarImage
                              src={document.profile_image_url || undefined}
                              alt="User avatar"
                            />
                            <AvatarFallback className="text-xs">
                              {getEmailInitial(document)}
                            </AvatarFallback>
                          </Avatar>
                          <span className="text-xs text-gray-500">
                            {getEmailText(document)}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 mb-2">
                          {new Date(document.created_at).toLocaleString(
                            locale,
                            {
                              month: "long",
                              day: "numeric",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: false,
                            },
                          )}
                        </div>
                        <div className="text-xs text-gray-600 mb-2 inline-flex items-center gap-1">
                          <Folder size={12} />
                          {document.folder_name || ROOT_FOLDER_LABEL}
                        </div>
                      </div>
                      <button
                        onClick={() => handleDelete(document.id)}
                        className="p-2 hover:bg-slate-100 rounded-md bg-transparent border-none focus:outline-none active:outline-none text-red-500 hover:text-red-700 transition-colors flex-shrink-0"
                        disabled={
                          document.status === "processing" ||
                          document.status === "pending"
                        }
                      >
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M3 6H5H21M19 6V20C19 21.1046 18.1046 22 17 22H7C5.89543 22 5 21.1046 5 20V6M8 6V4C8 2.89543 8.89543 2 10 2H14C15.1046 2 16 2.89543 16 4V6"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    </div>
                    {document.tools && document.tools.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-2">
                        {document.tools.map((tool) => (
                          <span
                            key={tool}
                            className="inline-block text-xs px-2 py-0.5 rounded-full"
                            style={{
                              backgroundColor: `${APP_COLOR}15`,
                              color: APP_COLOR,
                            }}
                          >
                            {tool}
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      {getStatusBadge(document.status)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Desktop Table Container */}
          <div className="hidden lg:block bg-white border border-gray-200 rounded-2xl overflow-hidden">
            {/* Table Header */}
            <div className={`${desktopTableGridClass} bg-gray-50 text-gray-700 border-b border-gray-200 text-sm`}>
              <div className="px-2 py-2 font-medium flex items-center gap-1 min-w-0">
                {t("documents.filename")} <div className="flex flex-col">
                  <div className="cursor-pointer hover:bg-gray-200 rounded px-1 py-0" onClick={() => handleSort('name', 'asc')}>
                    <ChevronUp size={16} className={sortField === 'name' && sortDirection === 'asc' ? "" : "text-gray-400"} style={sortField === 'name' && sortDirection === 'asc' ? { color: APP_COLOR } : undefined} />
                  </div>
                  <div className="cursor-pointer hover:bg-gray-200 rounded px-1 py-0" onClick={() => handleSort('name', 'desc')}>
                    <ChevronDown size={16} className={sortField === 'name' && sortDirection === 'desc' ? "" : "text-gray-400"} style={sortField === 'name' && sortDirection === 'desc' ? { color: APP_COLOR } : undefined} />
                  </div>
                </div>
              </div>
              <div className="px-2 py-2 font-medium flex items-center gap-1 min-w-0">
                {t("documents.uploadedBy")}{" "}
                <div className="flex flex-col">
                  <div className="cursor-pointer hover:bg-gray-200 rounded px-1 py-0" onClick={() => handleSort('email', 'asc')}>
                    <ChevronUp size={16} className={sortField === 'email' && sortDirection === 'asc' ? "" : "text-gray-400"} style={sortField === 'email' && sortDirection === 'asc' ? { color: APP_COLOR } : undefined} />
                  </div>
                  <div className="cursor-pointer hover:bg-gray-200 rounded px-1 py-0" onClick={() => handleSort('email', 'desc')}>
                    <ChevronDown size={16} className={sortField === 'email' && sortDirection === 'desc' ? "" : "text-gray-400"} style={sortField === 'email' && sortDirection === 'desc' ? { color: APP_COLOR } : undefined} />
                  </div>
                </div>
              </div>
              <div className="px-2 py-2 font-medium flex items-center gap-1 min-w-0">
                {t("documents.datetime")} <div className="flex flex-col">
                  <div className="cursor-pointer hover:bg-gray-200 rounded px-1 py-0" onClick={() => handleSort('date', 'asc')}>
                    <ChevronUp size={16} className={sortField === 'date' && sortDirection === 'asc' ? "" : "text-gray-400"} style={sortField === 'date' && sortDirection === 'asc' ? { color: APP_COLOR } : undefined} />
                  </div>
                  <div className="cursor-pointer hover:bg-gray-200 rounded px-1 py-0" onClick={() => handleSort('date', 'desc')}>
                    <ChevronDown size={16} className={sortField === 'date' && sortDirection === 'desc' ? "" : "text-gray-400"} style={sortField === 'date' && sortDirection === 'desc' ? { color: APP_COLOR } : undefined} />
                  </div>
                </div>
              </div>
              <div className="px-2 py-2 font-medium min-w-0">{t("documents.categories")}</div>
              <div className="px-2 py-2 font-medium min-w-0">{t("documents.status")}</div>
              <div className="px-2 py-2 font-medium min-w-0">{t("documents.actions")}</div>
            </div>

            {/* Table Rows */}
            {selectedChildFolders.map((folder) => (
              <div
                key={`folder-row-${folder.id}`}
                className={`${desktopTableGridClass} border-b border-gray-200`}
                style={{ backgroundColor: `${APP_COLOR}08` }}
              >
                <div
                  className="px-2 py-2 flex items-center gap-2 cursor-pointer transition-colors group min-w-0 text-sm"
                  onClick={() => {
                    setSelectedFolder(folder.id);
                    setCurrentPage(1);
                  }}
                >
                  <div
                    className="flex-shrink-0 p-1 rounded-md"
                    style={{ backgroundColor: `${APP_COLOR}20`, color: APP_COLOR }}
                  >
                    <Folder className="w-4 h-4" />
                  </div>
                  <div className="flex flex-col min-w-0">
                    <span className="truncate font-medium group-hover:underline">
                      {folder.name}
                    </span>
                    <span className="text-xs text-gray-600 truncate">
                      {t("documents.subfolder")}
                    </span>
                  </div>
                </div>
                <div className="px-2 py-2 flex items-center text-gray-500 text-sm">—</div>
                <div className="px-2 py-2 flex items-center text-gray-500 text-sm">—</div>
                <div className="px-2 py-2 flex items-center text-gray-500 text-sm min-w-0">
                  {folder.documents_count ?? 0} {t("documents.document_other")}
                </div>
                <div
                  className="px-2 py-2 flex items-center text-sm font-medium"
                  style={{ color: APP_COLOR }}
                >
                  {t("documents.folder")}
                </div>
                <div className="px-2 py-2 flex items-center">
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedFolder(folder.id);
                      setCurrentPage(1);
                    }}
                    className="inline-flex items-center justify-center whitespace-nowrap rounded-[1234px] text-xs font-medium border border-gray-300 bg-transparent hover:bg-slate-100 text-gray-700 h-7 px-2 py-1"
                  >
                    {t("documents.open")}
                  </button>
                </div>
              </div>
            ))}
            {isDocumentsLoading && documents.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Loader2 size={24} className="mx-auto mb-2 animate-spin" />
                {t("documents.loading")}
              </div>
            ) : null}
            {displayedDocuments.map((document) => (
              <div
                key={document.id}
                className={`${desktopTableGridClass} border-b border-gray-200`}
              >
                <div 
                  className="px-2 py-2 flex items-center gap-2 cursor-pointer transition-colors group min-w-0 text-sm"
                  onClick={() => handleDocumentClick(document.id, document.name)}
                  onMouseEnter={(e) => e.currentTarget.style.color = APP_COLOR}
                  onMouseLeave={(e) => e.currentTarget.style.color = 'inherit'}
                  title="Click to view/download"
                >
                  <div className="flex-shrink-0 p-1 rounded-md" style={{ backgroundColor: `${APP_COLOR}20` }}>
                    <FileText className="w-4 h-4" style={{ color: APP_COLOR }} />
                  </div>
                  <div className="flex flex-col min-w-0">
                    <span className="truncate group-hover:underline">{document.name}</span>
                  </div>
                </div>
                <div className="px-2 py-2 flex items-center gap-2 min-w-0 text-sm">
                  <Avatar className="w-7 h-7">
                    <AvatarImage
                      src={document.profile_image_url || undefined}
                      alt="User avatar"
                    />
                    <AvatarFallback>
                      {getEmailInitial(document)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="truncate">{getEmailText(document)}</span>
                </div>
                <div className="px-2 py-2 flex items-center text-gray-600 min-w-0 text-xs">
                  {new Date(document.created_at).toLocaleString(locale, {
                    day: "2-digit",
                    month: "2-digit",
                    year: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: false,
                  })}
                </div>
                <div className="px-2 py-2 flex items-center flex-wrap gap-1 min-w-0 overflow-hidden">
                  {document.tools && document.tools.length > 0 ? (
                    <>
                      {(expandedTagsByDocument[document.id]
                        ? document.tools
                        : document.tools.slice(0, 2)
                      ).map((tool) => (
                        <span
                          key={tool}
                          className="inline-block text-[11px] px-1.5 py-0.5 rounded-full max-w-full truncate"
                          style={{
                            backgroundColor: `${APP_COLOR}15`,
                            color: APP_COLOR,
                          }}
                          title={tool}
                        >
                          {tool}
                        </span>
                      ))}
                      {document.tools.length > 2 && (
                        <button
                          type="button"
                          className="inline-flex items-center text-[11px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200"
                          onClick={() =>
                            setExpandedTagsByDocument((prev) => ({
                              ...prev,
                              [document.id]: !prev[document.id],
                            }))
                          }
                        >
                          {expandedTagsByDocument[document.id]
                            ? t("users.less")
                            : `+${document.tools.length - 2}`}
                        </button>
                      )}
                    </>
                  ) : (
                    <span className="text-gray-400 text-xs">—</span>
                  )}
                </div>
                <div className="px-2 py-2 flex items-center min-w-0">
                  {getStatusBadge(document.status)}
                </div>
                <div className="px-2 py-2 flex items-center min-w-0 justify-center">
                  <button
                    onClick={() => handleDelete(document.id)}
                    className="p-2 hover:bg-slate-100 rounded-md bg-transparent border-none focus:outline-none active:outline-none text-red-500 hover:text-red-700 transition-colors"
                    disabled={
                      document.status === "processing" ||
                      document.status === "pending"
                    }
                  >
                    <svg
                      width="22"
                      height="22"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M3 6H5H21M19 6V20C19 21.1046 18.1046 22 17 22H7C5.89543 22 5 21.1046 5 20V6M8 6V4C8 2.89543 8.89543 2 10 2H14C15.1046 2 16 2.89543 16 4V6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            ))}

            {/* Pagination (Within Table) */}
            {totalPages > 1 ? (
              <div className="flex items-center justify-between p-4 bg-gray-50">
                {/* Left side: arrows + page numbers */}
                <div className="flex items-center gap-5">
                  {/* Previous button */}
                  <button
                    type="button"
                    onClick={() => {
                      if (currentPage > 1) setCurrentPage(previousPage);
                    }}
                    disabled={currentPage === 1}
                    className="text-600 disabled:opacity-50 hover:text-gray-700 hover:bg-slate-100 rounded-md px-3 py-1 transition-colors bg-transparent hover:border-transparent disabled:cursor-not-allowed"
                  >
                    &lt; {t("common.previous")}
                  </button>

                  {/* Page Numbers */}
                  <div className="flex items-center gap-3">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                      (page) => (
                        <span
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`cursor-pointer ${
                            page === currentPage
                              ? "w-8 h-8 flex items-center justify-center rounded-full"
                              : "text-gray-700 hover:text-gray-900"
                          }`}
                          style={
                            page === currentPage
                              ? {
                                  color: APP_COLOR,
                                  backgroundColor: `${APP_COLOR}20`,
                                }
                              : undefined
                          }
                        >
                          {page}
                        </span>
                      ),
                    )}
                  </div>

                  {/* Next button */}
                  <button
                    type="button"
                    onClick={() => {
                      if (currentPage < totalPages) setCurrentPage(nextPage);
                    }}
                    disabled={currentPage === totalPages}
                    className="text-600 disabled:opacity-50 hover:text-gray-700 hover:bg-slate-100 rounded-md px-3 py-1 transition-colors bg-transparent hover:border-transparent disabled:cursor-not-allowed"
                  >
                    {t("common.next")} &gt;
                  </button>
                </div>

                {/* Right side: "Showing x of y results" */}
                <div className="text-sm text-gray-500">
                  {t("documents.showingResults", { shown: displayedDocuments.length, total: totalDocuments })}
                </div>
              </div>
            ) : (
              <div className="p-4 bg-gray-50 text-center">
                <span className="text-sm text-gray-500">
                  {t("documents.allOnOnePage", {
                    count: totalDocuments,
                    label: t(totalDocuments === 1 ? "documents.document_one" : "documents.document_other"),
                  })}
                </span>
              </div>
            )}
          </div>

          {/* Mobile Pagination */}
          <div className="lg:hidden mt-6 pb-4 mb-20">
            {totalPages > 1 ? (
              <div className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      if (currentPage > 1) setCurrentPage(previousPage);
                    }}
                    disabled={currentPage === 1}
                    className="text-sm text-gray-600 disabled:opacity-50 hover:text-gray-700 px-3 py-1 rounded-md transition-colors disabled:cursor-not-allowed"
                  >
                    &lt; {t("common.previous")}
                  </button>
                  <span className="text-sm text-gray-500">
                    {t("documents.pageOf", { current: currentPage, total: totalPages })}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      if (currentPage < totalPages) setCurrentPage(nextPage);
                    }}
                    disabled={currentPage === totalPages}
                    className="text-sm text-gray-600 disabled:opacity-50 hover:text-gray-700 px-3 py-1 rounded-md transition-colors disabled:cursor-not-allowed"
                  >
                    {t("common.next")} &gt;
                  </button>
                </div>
                <div className="text-xs text-gray-500">
                  {documents.length} von {totalDocuments}
                </div>
              </div>
            ) : (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                <span className="text-sm text-gray-500">
                  {t("documents.allOnOnePage", {
                    count: totalDocuments,
                    label: t(totalDocuments === 1 ? "documents.document_one" : "documents.document_other"),
                  })}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {isCreateFolderModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-[460px] relative">
            <div className="p-4 lg:p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">{t("documents.createFolder")}</h2>
                <button
                  onClick={() => setIsCreateFolderModalOpen(false)}
                  className="p-2 hover:bg-slate-100 rounded-md bg-transparent border-none text-black hover:text-gray-700 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
              <div className="space-y-3">
                <div className="grid gap-2">
                  <Label htmlFor="new-folder-name">{t("documents.folderName")}</Label>
                  <Input
                    id="new-folder-name"
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    placeholder={t("documents.folderNamePlaceholder")}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") createFolder();
                    }}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="new-folder-parent">{t("documents.parentFolder")}</Label>
                  <select
                    id="new-folder-parent"
                    value={newFolderParentId}
                    onChange={(e) => setNewFolderParentId(e.target.value)}
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900"
                  >
                    <option value="">{t("documents.topLevel")}</option>
                    {flattenFolders.map((folder) => (
                      <option key={folder.id} value={folder.id}>
                        {`${"  ".repeat(folder.depth)}${folder.name}`}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-5">
                <button
                  onClick={() => setIsCreateFolderModalOpen(false)}
                  className="inline-flex items-center justify-center whitespace-nowrap rounded-[1234px] text-sm font-medium border border-gray-300 bg-transparent hover:bg-slate-100 text-gray-700 h-10 px-4 py-2"
                >
                  {t("common.cancel")}
                </button>
                <button
                  onClick={createFolder}
                  className="inline-flex h-10 items-center justify-center gap-1 whitespace-nowrap rounded-[1234px] border px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
                  style={{
                    backgroundColor: APP_COLOR,
                    borderColor: APP_COLOR,
                  }}
                  disabled={!newFolderName.trim()}
                >
                  <FolderPlus size={16} />
                  {t("documents.create")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Website Crawl Modal */}
      {isWebsiteCrawlModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-[560px] relative max-h-[90vh] overflow-y-auto">
            <div className="p-4 lg:p-6">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold">Website crawl</h2>
                  <p className="text-sm text-gray-500">
                    Crawls one domain into <span className="font-medium">EXTERNE QUELLEN</span> with its
                    linked PDFs, then auto-refreshes nightly when content changes.
                  </p>
                </div>
                <button
                  onClick={() => setIsWebsiteCrawlModalOpen(false)}
                  disabled={isStartingWebsiteCrawl}
                  className="rounded-md bg-transparent p-2 text-black transition-colors hover:bg-slate-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="website-url">Website URL</Label>
                  <Input
                    id="website-url"
                    value={websiteUrl}
                    onChange={(e) => setWebsiteUrl(e.target.value)}
                    placeholder="https://example.com"
                    disabled={isStartingWebsiteCrawl}
                  />
                </div>

                <div className="grid gap-2">
                  <Label className="mb-1 block">
                    {isSpedlogSession ? t("documents.categoriesRequired") : t("documents.categoriesOptional")}
                  </Label>
                  <div className="grid grid-cols-2 gap-2">
                    {availableToolCategories.map((cat) => (
                      <label
                        key={`crawl-${cat}`}
                        className="flex cursor-pointer select-none items-center gap-2 text-sm"
                      >
                        <input
                          type="checkbox"
                          className="rounded border-gray-300"
                          checked={selectedTools.includes(cat)}
                          onChange={() => {
                            setSelectedTools((prev) =>
                              prev.includes(cat)
                                ? prev.filter((item) => item !== cat)
                                : [...prev, cat],
                            );
                          }}
                          disabled={isStartingWebsiteCrawl}
                        />
                        {cat}
                      </label>
                    ))}
                  </div>
                </div>

                {websiteCrawlJob && (
                  <div className="rounded-md border border-gray-200 bg-slate-50 p-3 text-sm">
                    <div className="mb-2 flex items-center justify-between gap-3">
                      <span className="font-medium">{websiteCrawlJob.domain}</span>
                      <div className="flex items-center gap-2">
                        <span className="rounded-full bg-white px-2 py-1 text-xs text-gray-600">
                          {formatCrawlPhase(websiteCrawlJob.status)}
                        </span>
                        {activeWebsiteCrawlJob && (
                          <button
                            type="button"
                            onClick={handleCancelWebsiteCrawl}
                            disabled={
                              isCancellingWebsiteCrawl ||
                              websiteCrawlJob.status === "cancelling"
                            }
                            className="inline-flex h-7 items-center gap-1 rounded-md border border-red-200 bg-white px-2 text-xs font-medium text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            <X size={12} />
                            Stop
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 sm:grid-cols-4">
                      <span>Pages: {websiteCrawlJob.pages_crawled} / {websiteCrawlJob.pages_found || 500}</span>
                      <span>PDFs: {websiteCrawlJob.pdfs_downloaded} / {websiteCrawlJob.pdfs_found}</span>
                      <span>Files: {websiteCrawlJob.files_uploaded}</span>
                      <span>Folder: {websiteCrawlJob.folder_id ? "created" : "pending"}</span>
                    </div>
                    {websiteCrawlJob.error && (
                      <p className="mt-2 text-xs text-red-600">{websiteCrawlJob.error}</p>
                    )}
                    {websiteCrawlBlocksStart && websiteCrawlJob.folder_id && (
                      <p className="mt-2 text-xs text-gray-600">
                        Delete this website folder before crawling the same domain again.
                      </p>
                    )}
                  </div>
                )}
              </div>

              <div className="mt-5 flex flex-col justify-end gap-2 sm:flex-row">
                <button
                  className="order-2 inline-flex h-10 items-center justify-center rounded-[1234px] border border-gray-300 bg-transparent px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-slate-100 sm:order-1"
                  onClick={() => setIsWebsiteCrawlModalOpen(false)}
                  disabled={isStartingWebsiteCrawl}
                >
                  Close
                </button>
                <button
                  className="order-1 inline-flex h-10 items-center justify-center rounded-[1234px] border px-4 py-2 text-sm font-medium text-white transition-colors disabled:pointer-events-none disabled:opacity-50 sm:order-2"
                  style={{
                    backgroundColor: APP_COLOR,
                    borderColor: APP_COLOR,
                  }}
                  onClick={handleStartWebsiteCrawl}
                  disabled={
                    isStartingWebsiteCrawl ||
                    websiteCrawlBlocksStart ||
                    !websiteUrl.trim() ||
                    (isSpedlogSession && selectedTools.length === 0)
                  }
                >
                  {isStartingWebsiteCrawl ? (
                    <>
                      <Loader2 size={16} className="mr-2 animate-spin" />
                      Starting crawler
                    </>
                  ) : (
                    <>
                      <Globe size={16} className="mr-2" />
                      Start crawl
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {isUploadModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-[500px] relative max-h-[90vh] overflow-y-auto">
            <div className="p-4 lg:p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">
                  {uploadFiles.length === 0
                    ? t("documents.uploadDocumentNew")
                    : uploadFiles.length === 1
                      ? t("documents.uploadDocumentSingle")
                      : t("documents.uploadDocumentMulti", { count: uploadFiles.length })}
                </h2>
                <button
                  onClick={closeUploadModal}
                  disabled={isUploading}
                  className="p-2 hover:bg-slate-100 rounded-md bg-transparent border-none focus:outline-none active:outline-none text-black hover:text-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <X size={20} />
                </button>
              </div>
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="target-folder">{t("documents.folder")}</Label>
                  <select
                    id="target-folder"
                    value={uploadTargetFolderId}
                    onChange={(e) => setUploadTargetFolderId(e.target.value)}
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900"
                    disabled={isUploading}
                  >
                    <option value="">{ROOT_FOLDER_LABEL}</option>
                    {flattenFolders.map((folder) => (
                      <option key={folder.id} value={folder.id}>
                        {`${"  ".repeat(folder.depth)}${folder.name}`}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="file">{t("documents.selectFiles")}</Label>
                  <div
                    className="border-2 border-dashed border-gray-300 rounded-lg p-4 lg:p-6 text-center"
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragEnter={handleDragEnter}
                  >
                    {uploadFiles.length > 0 ? (
                      <div className="space-y-2">
                        <div className="text-sm font-medium">
                          {t("documents.filesSelected", { count: uploadFiles.length })}
                        </div>
                        <div className="max-h-32 overflow-y-auto space-y-1">
                          {uploadFiles.map((file, index) => (
                            <div
                              key={index}
                              className="flex items-center justify-between bg-gray-50 p-2 rounded"
                            >
                              <div className="flex items-center gap-2 min-w-0 flex-1">
                                <FileText size={14} className="flex-shrink-0" />
                                <span className="text-sm truncate">
                                  {file.name}
                                </span>
                                <span className="text-xs text-gray-500 flex-shrink-0">
                                  ({(file.size / 1024 / 1024).toFixed(1)} MB)
                                </span>
                              </div>
                              <button
                                type="button"
                                onClick={() => removeFile(index)}
                                className="p-1 hover:bg-gray-200 rounded flex-shrink-0"
                                disabled={isUploading}
                              >
                                <X size={14} />
                              </button>
                            </div>
                          ))}
                        </div>
                        <div className="flex justify-center gap-2 mt-2">
                          <button
                            className="inline-flex items-center justify-center whitespace-nowrap rounded-[1234px] text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground hover:border-transparent h-8 px-3"
                            onClick={clearAllFiles}
                            disabled={isUploading}
                          >
                            {t("documents.removeAll")}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <p className="text-sm text-gray-500 mb-2">
                          {t("documents.dropFiles")}
                        </p>
                        <Input
                          id="file"
                          type="file"
                          className="hidden"
                          multiple
                          onChange={(e) => handleFileSelect(e)}
                          accept=".pdf,.doc,.docx,.xlsx,.csv,.ifc"
                        />
                        <button
                          className="inline-flex items-center justify-center whitespace-nowrap rounded-[1234px] text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border bg-transparent h-9 px-3"
                          style={{ 
                            borderColor: APP_COLOR,
                            color: APP_COLOR
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = `${APP_COLOR}10`;
                            e.currentTarget.style.color = APP_COLOR;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = APP_COLOR;
                          }}
                          onClick={() =>
                            document.getElementById("file")?.click()
                          }
                        >
                          {t("documents.browseFiles")}
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="mt-3">
                    <Label className="mb-2 block">
                      {isSpedlogSession ? t("documents.categoriesRequired") : t("documents.categoriesOptional")}
                    </Label>
                    <div className="grid grid-cols-2 gap-2">
                      {availableToolCategories.map((cat) => (
                        <label
                          key={cat}
                          className="flex items-center gap-2 text-sm cursor-pointer select-none"
                        >
                          <input
                            type="checkbox"
                            className="rounded border-gray-300"
                            checked={selectedTools.includes(cat)}
                            onChange={() => {
                              setSelectedTools((prev) =>
                                prev.includes(cat)
                                  ? prev.filter((t) => t !== cat)
                                  : [...prev, cat]
                              );
                            }}
                          />
                          {cat}
                        </label>
                      ))}
                    </div>
                    {isSpedlogSession && availableToolCategories.length === 0 && (
                      <p className="text-xs text-red-600 mt-2">
                        {t("documents.noAllowedCategories")}
                      </p>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    {t("documents.supportedFormats")}
                  </p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row justify-end gap-2 mt-4">
                <button
                  className="inline-flex items-center justify-center whitespace-nowrap rounded-[1234px] text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-gray-300 bg-transparent hover:bg-slate-100 text-gray-700 hover:text-gray-700 h-10 px-4 py-2 order-2 sm:order-1"
                  onClick={closeUploadModal}
                  disabled={isUploading}
                >
                  {t("common.cancel")}
                </button>
                <button
                  className="order-1 inline-flex h-10 items-center justify-center whitespace-nowrap rounded-[1234px] border px-4 py-2 text-sm font-medium text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 sm:order-2"
                  style={{
                    backgroundColor: APP_COLOR,
                    borderColor: APP_COLOR,
                  }}
                  onClick={handleUpload}
                  disabled={
                    uploadFiles.length === 0 ||
                    isUploading ||
                    (isSpedlogSession && selectedTools.length === 0) ||
                    (isSpedlogSession && availableToolCategories.length === 0)
                  }
                >
                  {isUploading ? (
                    <>
                      <Loader2 size={16} className="mr-2 animate-spin" />
                      {t("documents.uploadingFiles", {
                        count: uploadFiles.length,
                        suffix: uploadFiles.length !== 1 ? "s" : "",
                      })}
                    </>
                  ) : (
                    <>
                      <Upload size={16} className="mr-2" />
                      {uploadFiles.length === 1
                        ? t("documents.uploadSingleFile")
                        : t("documents.uploadMultiFiles", { count: uploadFiles.length })}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Profile Image Modal */}
      <ProfileImageModal
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
        currentUser={currentUser}
        onProfileImageUpdate={handleProfileImageUpdate}
        onUserInfoUpdate={handleUserInfoUpdate}
      />
    </div>
  );
}
